import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui';
import { cn } from '@/lib/utils/cn';
import { formatDate, formatTime } from '@/lib/utils/date';
import type { NomenclatureResolver } from '@/lib/nomenclature/resolver';
import type { Appointment } from '../types/appointment.types';

interface AppointmentCardProps {
  appointment: Appointment;
  variant: 'upcoming' | 'past';
  resolver: NomenclatureResolver | null;
}

/**
 * One appointment card — same information the mobile card shows: a status mark,
 * the physician (with degree/title) + specialty, the date & time, and the
 * location. Physician / specialty / location / degree are MEDIS UUIDs resolved
 * to names via the nomenclature resolver.
 */
export function AppointmentCard({ appointment, variant, resolver }: AppointmentCardProps) {
  const { t } = useTranslation();
  const { startTime, endTime } = appointment;

  const name = resolver?.physicianName(appointment.physicianId) ?? null;
  // The appointment carries a degree UUID directly; fall back to the physician's
  // main degree, then to the raw `degree` value.
  const degree =
    resolver?.degreeName(appointment.degree) ??
    resolver?.physicianDegree(appointment.physicianId) ??
    (appointment.degree || null);
  const specialty = resolver?.specialtyName(appointment.specialityId) ?? null;
  const location = resolver?.locationName(appointment.locationId) ?? null;

  const physicianLabel = name ? (degree ? `${degree} ${name}` : name) : t('appointments.consultation');

  return (
    <Card className="flex flex-col gap-3 p-4">
      <div className="flex items-start gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary-light text-primary">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold text-ink">{physicianLabel}</p>
          {specialty && <p className="truncate text-sm text-ink-mute">{specialty}</p>}
        </div>
        <span
          className={cn(
            'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-white',
            variant === 'upcoming' ? 'bg-primary' : 'bg-success',
          )}
          aria-label={variant === 'upcoming' ? t('appointments.upcoming_one') : t('appointments.past_one')}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
            <path d="M20 6 9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </div>

      <div className="flex flex-wrap items-end justify-between gap-3 border-t border-muted-200 pt-3">
        <div className="flex flex-col gap-1">
          <span className="text-xs font-semibold uppercase tracking-wide text-ink-mute">
            {t('appointments.date_label')}
          </span>
          <span className="flex items-center gap-1.5 text-sm font-semibold text-ink">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-primary">
              <path d="M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {formatDate(startTime)}
          </span>
          <span className="flex items-center gap-1.5 text-sm text-ink-soft">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-primary">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7v5l3 2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {formatTime(startTime)} – {formatTime(endTime)}
          </span>
        </div>

        {location && (
          <div className="flex max-w-[55%] flex-col items-end gap-1 text-right">
            <span className="text-xs font-semibold uppercase tracking-wide text-ink-mute">
              {t('appointments.location_label')}
            </span>
            <span className="text-sm font-semibold text-ink">{location}</span>
          </div>
        )}
      </div>
    </Card>
  );
}
